import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DraftCardShell, { outlineButtonSx, primaryButtonSx, textButtonSx } from "./DraftCardShell";
import { AI_ASSISTANT_BLUE } from "../constants";

function SectionRow({ section, index, total, disabled, actions }) {
  const [open, setOpen] = useState(false);
  const [heading, setHeading] = useState(section.heading);
  const [body, setBody] = useState(section.body);
  const [rewriting, setRewriting] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [problem, setProblem] = useState("");

  useEffect(() => setHeading(section.heading), [section.heading]);
  useEffect(() => setBody(section.body), [section.body]);

  const commit = () => {
    if (heading !== section.heading || body !== section.body) actions.update(section.id, { heading, body });
  };

  const rewrite = async () => {
    commit();
    setRewriting(true);
    setProblem("");
    const err = await actions.rewrite(section.id, instruction || "Make it clearer and more concise for students.");
    setRewriting(false);
    if (err) setProblem(err);
    else setInstruction("");
  };

  return (
    <Box sx={{ p: 1, borderRadius: 2, border: "1px solid", borderColor: open ? "rgba(0,97,254,0.45)" : "rgba(148,163,184,0.3)" }}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Box role="button" tabIndex={0} onClick={() => setOpen((v) => !v)} onKeyDown={(e) => e.key === "Enter" && setOpen((v) => !v)} sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
          <Typography noWrap fontWeight={700} sx={{ fontSize: 12.5, color: "#0f172a" }}>
            {section.heading || `Section ${index + 1}`}
          </Typography>
          {!open ? (
            <Typography noWrap sx={{ fontSize: 11.5, color: "#64748b" }}>{section.body || "Empty"}</Typography>
          ) : null}
        </Box>
        <IconButton size="small" aria-label="Move section up" disabled={disabled || index === 0} onClick={() => actions.move(section.id, -1)}>
          <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" aria-label="Move section down" disabled={disabled || index >= total - 1} onClick={() => actions.move(section.id, 1)}>
          <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" aria-label="Remove section" disabled={disabled} onClick={() => actions.remove(section.id)} sx={{ color: "#b91c1c" }}>
          <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Stack>
      {open ? (
        <Stack spacing={0.8} sx={{ mt: 0.75 }}>
          <TextField size="small" label="Heading" value={heading} disabled={disabled} onChange={(e) => setHeading(e.target.value)} onBlur={commit} fullWidth />
          <TextField size="small" label="Text" value={body} disabled={disabled || rewriting} onChange={(e) => setBody(e.target.value)} onBlur={commit} fullWidth multiline minRows={3} maxRows={14} />
          <Stack direction="row" spacing={0.75} alignItems="center">
            <TextField
              size="small"
              fullWidth
              placeholder="Rewrite: simpler, shorter, add clinical relevance…"
              value={instruction}
              disabled={disabled || rewriting}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && rewrite()}
              inputProps={{ "aria-label": "Rewrite instruction" }}
            />
            <Tooltip title="Rewrite this section with AI">
              <span>
                <IconButton aria-label="Rewrite with AI" disabled={disabled || rewriting || !body.trim()} onClick={rewrite} sx={{ color: AI_ASSISTANT_BLUE }}>
                  {rewriting ? <CircularProgress size={16} /> : <AutoFixHighRoundedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {problem ? <Typography sx={{ fontSize: 11.5, color: "#b91c1c" }}>{problem}</Typography> : null}
        </Stack>
      ) : null}
    </Box>
  );
}

SectionRow.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  actions: PropTypes.object.isRequired,
};

/**
 * Editable working copy of an uploaded document. Save updates only the text the
 * assistant reads; the uploaded original is never overwritten or deleted.
 */
export default function DocumentDraftCard({ doc, actions, disabled }) {
  const [expanded, setExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");
  if (!doc) return null;
  const sections = doc.sections || [];
  const busy = disabled || saving;

  const save = async () => {
    setSaving(true);
    setProblem("");
    const err = await actions.save();
    setSaving(false);
    if (err) setProblem(err);
  };

  return (
    <DraftCardShell
      kicker={doc.dirty ? "DOCUMENT · UNSAVED CHANGES" : "DOCUMENT"}
      title={doc.file_name || "Document"}
      summary={`${sections.length} section${sections.length === 1 ? "" : "s"} · temporary draft — your uploaded file is not changed`}
      icon={<DescriptionOutlinedIcon fontSize="small" />}
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
      headerAction={
        <Tooltip title="Close document">
          <IconButton size="small" aria-label="Close document" onClick={actions.close}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
      footer={
        <Stack spacing={0.75}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="contained" disableElevation disabled={busy} onClick={() => actions.useFor("presentation")} sx={primaryButtonSx}>
              Make presentation
            </Button>
            <Button size="small" variant="outlined" disabled={busy} onClick={() => actions.useFor("quiz")} sx={outlineButtonSx}>
              Make quiz
            </Button>
            {doc.dirty ? (
              <>
                <Button size="small" disabled={busy} onClick={save} sx={textButtonSx}>
                  {saving ? "Saving…" : "Save"}
                </Button>
                <Button size="small" disabled={busy} onClick={actions.discard} sx={{ ...textButtonSx, color: "#64748b" }}>
                  Discard changes
                </Button>
              </>
            ) : null}
          </Stack>
          {problem ? <Typography sx={{ fontSize: 11.5, color: "#b91c1c" }}>{problem}</Typography> : null}
        </Stack>
      }
    >
      <Stack spacing={0.6} sx={{ maxHeight: 420, overflowY: "auto", pr: 0.25 }}>
        {sections.map((section, index) => (
          <SectionRow key={section.id} section={section} index={index} total={sections.length} disabled={busy} actions={actions} />
        ))}
      </Stack>
      <Button size="small" startIcon={<AddRoundedIcon fontSize="small" />} disabled={busy} onClick={() => actions.add(null)} sx={{ ...textButtonSx, mt: 0.75 }}>
        Add section
      </Button>
    </DraftCardShell>
  );
}

DocumentDraftCard.propTypes = {
  doc: PropTypes.object,
  actions: PropTypes.shape({
    update: PropTypes.func.isRequired,
    remove: PropTypes.func.isRequired,
    add: PropTypes.func.isRequired,
    move: PropTypes.func.isRequired,
    rewrite: PropTypes.func.isRequired,
    save: PropTypes.func.isRequired,
    discard: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    useFor: PropTypes.func.isRequired,
  }).isRequired,
  disabled: PropTypes.bool,
};
